import {
  type AlmanacGroup,
  useAlmanacOptions,
} from "portal/components/categoryTemplates/useAlmanacOptions";
import { FormikConfig } from "formik";
import { MOST_SIMILAR_TO_OPTIONS } from "portal/utils/categoryCollection/category";
import { object, string } from "yup";
import { resolveAlmanacCategory } from "../resolveAlmanacCategory";
import { useCreateCategoryTemplate } from "../useCreateCategoryTemplate";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export type AddNewCategoryFormValues = {
  name: string;
  sourceAlmanacId: string;
  mostSimilarTo: string;
};

export type UseAddNewCategoryFormResult = {
  almanacGroups: AlmanacGroup[];
  defaultAlmanacId: string;
  formConfig: FormikConfig<AddNewCategoryFormValues>;
  isLoading: boolean;
};

export const useAddNewCategoryForm = (
  customerUuid: string | undefined,
  onSubmitSuccess: (newCategoryId: string) => void
): UseAddNewCategoryFormResult => {
  const { t } = useTranslation();
  const { almanacGroups, defaultAlmanacId, isLoading } =
    useAlmanacOptions(customerUuid);

  const createCategoryTemplate = useCreateCategoryTemplate(customerUuid);

  const initialValues: AddNewCategoryFormValues = useMemo(
    () => ({
      name: "",
      sourceAlmanacId: defaultAlmanacId,
      mostSimilarTo: MOST_SIMILAR_TO_OPTIONS[0],
    }),
    [defaultAlmanacId]
  );

  return {
    almanacGroups,
    defaultAlmanacId,
    isLoading,
    formConfig: {
      initialValues,
      validationSchema: object({
        name: string().trim().required(t("utils.form.required")),
        mostSimilarTo: string().required(t("utils.form.required")),
        sourceAlmanacId: string().required(t("utils.form.required")),
      }),
      onSubmit: async (
        { name, mostSimilarTo, sourceAlmanacId },
        { setFieldError }
      ) => {
        const resolved = resolveAlmanacCategory(
          almanacGroups,
          sourceAlmanacId,
          mostSimilarTo,
          t
        );
        if (!resolved.ok) {
          setFieldError(resolved.field, resolved.message);
          return;
        }

        const createdId = await createCategoryTemplate({
          name: name.trim(),
          chipIds: [],
          almanac: resolved.almanac,
        });
        if (createdId) {
          onSubmitSuccess(createdId);
        }
      },
    },
  };
};
